import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Zap, Heart, Brain, Users, Star, Lock, Unlock } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface PremiumFeaturesProps {
  onUpgrade?: () => void;
}

const premiumFeatures = [
  {
    id: 'ai-coach',
    title: 'Personal AI Relationship Coach',
    description: 'Get 24/7 access to your dedicated AI coach powered by Gemini 2.5 Flash',
    icon: Brain,
    color: 'from-purple-500 to-indigo-500',
    benefits: [
      'Personalized daily check-ins',
      'Custom relationship goals',
      'Advanced pattern recognition',
      'Predictive insights'
    ],
    demo: true
  },
  {
    id: 'vr-scenarios',
    title: 'Advanced VR Role-Play',
    description: 'Practice difficult conversations in immersive virtual environments',
    icon: Users,
    color: 'from-blue-500 to-cyan-500',
    benefits: [
      '50+ realistic scenarios',
      'AI-powered responses',
      'Emotional feedback',
      'Progress tracking'
    ],
    demo: true
  },
  {
    id: 'family-insights',
    title: 'Family Dynamics Analysis',
    description: 'Understand and improve relationships with family members',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    benefits: [
      'Multi-generational insights',
      'Conflict resolution strategies',
      'Communication style mapping',
      'Family goal setting'
    ],
    demo: false
  },
  {
    id: 'wearable-sync',
    title: 'Advanced Wearable Integration',
    description: 'Deep insights from your Apple Watch, Fitbit, and other devices',
    icon: Zap,
    color: 'from-green-500 to-teal-500',
    benefits: [
      'Stress correlation analysis',
      'Sleep quality impact',
      'Heart rate variability',
      'Mood prediction'
    ],
    demo: false
  }
];

export const PremiumFeatures: React.FC<PremiumFeaturesProps> = ({ onUpgrade }) => {
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [unlockedFeatures, setUnlockedFeatures] = useState<string[]>([]);
  const prefersReducedMotion = useReducedMotion();

  const handleFeatureUnlock = (featureId: string) => {
    if (!unlockedFeatures.includes(featureId)) {
      setUnlockedFeatures(prev => [...prev, featureId]);
      // Trigger confetti animation
      if (!prefersReducedMotion) {
        console.log('🎉 Feature unlocked! Confetti animation triggered');
      }
    }
  };

  const handleDemoStart = (featureId: string) => {
    setActiveDemo(featureId);
    handleFeatureUnlock(featureId);
  };

  return (
    <div className="py-24 bg-gradient-to-br from-purple-100 via-blue-50 to-pink-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-pink-900/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-6">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Premium Features{' '}
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              Unlocked
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Experience the full power of AI-driven relationship insights with these exclusive premium features.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {premiumFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              className="group relative"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={prefersReducedMotion ? {} : { y: -5 }}
            >
              <div className="h-full p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300">
                {/* Feature Status */}
                <div className="flex items-center justify-between mb-6">
                  <motion.div
                    className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${feature.color}`}
                    whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  {unlockedFeatures.includes(feature.id) ? (
                    <motion.div
                      className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full text-sm font-medium"
                      initial={prefersReducedMotion ? {} : { scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', duration: 0.5 }}
                    >
                      <Unlock className="w-4 h-4" />
                      Unlocked
                    </motion.div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-1 bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-full text-sm font-medium">
                      <Lock className="w-4 h-4" />
                      Premium
                    </div>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {feature.description}
                </p>

                {/* Benefits */}
                <ul className="space-y-3 mb-8">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <motion.li
                      key={benefit}
                      className="flex items-start gap-3"
                      initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + benefitIndex * 0.1 }}
                    >
                      <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* Action Button */}
                {feature.demo ? (
                  <motion.button
                    onClick={() => handleDemoStart(feature.id)}
                    className={`w-full py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      activeDemo === feature.id
                        ? 'bg-green-500 text-white'
                        : unlockedFeatures.includes(feature.id)
                        ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                        : `bg-gradient-to-r ${feature.color} text-white hover:shadow-lg`
                    }`}
                    whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                    whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                    disabled={activeDemo === feature.id}
                  >
                    {activeDemo === feature.id ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <Zap className="w-5 h-5" />
                        </motion.div>
                        Demo Active
                      </>
                    ) : unlockedFeatures.includes(feature.id) ? (
                      <>
                        <Unlock className="w-5 h-5" />
                        Feature Unlocked
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        Try Demo
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={() => handleFeatureUnlock(feature.id)}
                    className={`w-full py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      unlockedFeatures.includes(feature.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white border border-gray-200/30 dark:border-gray-700/30 hover:bg-white/20 dark:hover:bg-gray-800/20'
                    }`}
                    whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                    whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                  >
                    {unlockedFeatures.includes(feature.id) ? (
                      <>
                        <Unlock className="w-5 h-5" />
                        Unlocked
                      </>
                    ) : (
                      <>
                        <Crown className="w-5 h-5" />
                        Unlock Feature
                      </>
                    )}
                  </motion.button>
                )}

                {/* Glow Effect */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Upgrade CTA */}
        <motion.div
          className="text-center p-8 rounded-3xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-200/30 dark:border-purple-700/30"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Transform Your Relationships?
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Unlock all premium features and start your journey to deeper, more meaningful connections.
          </p>
          <motion.button
            onClick={onUpgrade}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg flex items-center gap-2 mx-auto"
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(168, 85, 247, 0.3)" 
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            <Crown className="w-5 h-5" />
            Upgrade to Premium
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};